import React, { useState, useEffect } from 'react';
import { Table, Typography, Input, Button, Tag, Segmented, Badge, Select, message } from 'antd';
import { SearchOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getTransmisiones, refreshTransmisiones, updateTransmision, getAsesores, type Transmision, type AsesorOption } from '../api/transmisionesApi';
import TransmisionDetailModal from '../components/TransmisionDetailModal';

const { Title } = Typography;

interface TransmisionDisplay extends Transmision {
  key: string;
}

interface EstadoCounts {
  enVenta: number;
  pendiente: number;
  realizada: number;
}

const getEstadoColor = (estadoId?: number) => {
  if (estadoId === undefined || estadoId === null) return 'default';
  // Estado 0 = En venta (verde pastel), 1,2,3 = Pendiente (naranja), 5 = Realizada (verde)
  if (estadoId === 0) return 'cyan';
  if (estadoId >= 1 && estadoId <= 3) return 'orange';
  if (estadoId === 5) return 'green';
  return 'default';
};

const getEstadoLabel = (estadoId?: number) => {
  if (estadoId === undefined || estadoId === null) return 'Sin estado';
  // Estado 0 = En venta, Estados 1,2,3 se unifican como "Pendiente"
  if (estadoId === 0) return 'En venta';
  if (estadoId >= 1 && estadoId <= 3) return 'Pendiente';
  if (estadoId === 5) return 'Realizada';
  return `Estado ${estadoId}`;
};

const formatTitulares = (vendedores?: { cliente?: { _Name: string } }[]) => {
  if (!vendedores || vendedores.length === 0) return '-';

  const clientes = vendedores.filter(v => v.cliente);
  if (clientes.length === 0) return '-';

  const primerNombre = clientes[0].cliente?._Name || 'Sin nombre';

  if (clientes.length === 1) {
    return primerNombre;
  } else {
    const adicionales = clientes.length - 1;
    return `${primerNombre} (y ${adicionales} más)`;
  }
};

const TransmisionesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [transmisiones, setTransmisiones] = useState<TransmisionDisplay[]>([]);
  const [filteredData, setFilteredData] = useState<TransmisionDisplay[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedEstado, setSelectedEstado] = useState<number>(3); // Default: Pendiente
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTransmision, setSelectedTransmision] = useState<Transmision | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [estadoCounts, setEstadoCounts] = useState<EstadoCounts>({
    enVenta: 0,
    pendiente: 0,
    realizada: 0
  });
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [asesores, setAsesores] = useState<AsesorOption[]>([]);
  const [selectedAsesor, setSelectedAsesor] = useState<number | undefined>(undefined);

  const loadTransmisiones = async (estadoId?: number) => {
    setLoading(true);
    try {
      const data = await getTransmisiones(estadoId);
      // DEBUG: Ver estructura de datos
      if (data.length > 0) {
        console.log('Primera transmisión:', data[0]);
        console.log('Farmacia:', data[0].farmacia);
        console.log('Vendedores:', data[0].vendedores);
      }
      const displayData = data.map(t => ({
        ...t,
        key: t.id.toString()
      }));
      setTransmisiones(displayData);
      applyFilters(displayData, searchText, selectedAsesor);
      message.success(`${displayData.length} transmisiones cargadas`);
    } catch (error) {
      message.error('Error al cargar transmisiones');
      console.error('Error loading transmisiones:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTransmisionCounts = async () => {
    setLoadingCounts(true);
    try {
      // Hacer llamadas paralelas para obtener los contadores de cada estado
      const [enVentaData, pendienteData, realizadaData] = await Promise.all([
        getTransmisiones(0),  // En venta
        getTransmisiones(3),  // Pendiente
        getTransmisiones(5)   // Realizada
      ]);

      setEstadoCounts({
        enVenta: enVentaData.length,
        pendiente: pendienteData.length,
        realizada: realizadaData.length
      });
    } catch (error) {
      console.error('Error loading transmision counts:', error);
      // Si falla, no mostrar error al usuario, solo en consola
    } finally {
      setLoadingCounts(false);
    }
  };

  useEffect(() => {
    loadTransmisiones(selectedEstado);
  }, [selectedEstado]);

  useEffect(() => {
    // Cargar contadores y catálogo de asesores al montar el componente
    loadTransmisionCounts();
    getAsesores().then(setAsesores);
  }, []);

  const applyFilters = (data: TransmisionDisplay[], text: string, asesorId?: number) => {
    let filtered = data;

    // Filtro por asesor
    if (asesorId !== undefined) {
      filtered = filtered.filter(item => item.responsableId === asesorId);
    }

    // Filtro por texto
    if (text) {
      const searchLower = text.toLowerCase();
      filtered = filtered.filter((item) => {
        const farmaciaMatch = item.farmacia?._Name?.toLowerCase().includes(searchLower);
        const provinciaMatch = item.provincia?.provincia?.toLowerCase().includes(searchLower);
        const vendedoresMatch = item.vendedores?.some(v =>
          v.cliente?._Name?.toLowerCase().includes(searchLower)
        );
        const compradoresMatch = item.compradores?.some(c =>
          c.cliente?._Name?.toLowerCase().includes(searchLower)
        );
        const notasMatch = item.notas?.toLowerCase().includes(searchLower);
        const origenMatch = item.origen?.toLowerCase().includes(searchLower);
        return farmaciaMatch || provinciaMatch || vendedoresMatch || compradoresMatch || notasMatch || origenMatch;
      });
    }

    setFilteredData(filtered);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters(transmisiones, value, selectedAsesor);
  };

  const handleAsesorChange = (value: number | undefined) => {
    setSelectedAsesor(value);
    applyFilters(transmisiones, searchText, value);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Actualizar datos y contadores en paralelo
      const [data] = await Promise.all([
        refreshTransmisiones(selectedEstado),
        loadTransmisionCounts()
      ]);

      const displayData = data.map(t => ({
        ...t,
        key: t.id.toString()
      }));
      setTransmisiones(displayData);
      setSearchText('');
      applyFilters(displayData, '', selectedAsesor);
      message.success('Listado actualizado');
    } catch (error) {
      message.error('Error al actualizar transmisiones');
      console.error('Error refreshing transmisiones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEstadoChange = (value: string | number) => {
    const estadoId = Number(value);
    setSelectedEstado(estadoId);
    setSearchText('');
  };

  const handleRowClick = (record: TransmisionDisplay) => {
    setSelectedTransmision(record);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedTransmision(null);
  };

  const handleSaveTransmision = async (id: number, fecha: string, estadoId: number, notasGestion?: string) => {
    try {
      await updateTransmision(id, fecha, estadoId, notasGestion);
      message.success('Transmisión actualizada correctamente');
      // Actualizar listado y contadores
      await Promise.all([
        loadTransmisiones(selectedEstado),
        loadTransmisionCounts()
      ]);
    } catch (error) {
      message.error('Error al actualizar la transmisión');
      throw error;
    }
  };

  const columns: ColumnsType<TransmisionDisplay> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: 'Fecha Registro',
      dataIndex: 'fechaCreacion',
      key: 'fechaCreacion',
      width: 120,
      render: (fecha: string) => new Date(fecha).toLocaleDateString('es-ES'),
      sorter: (a, b) => new Date(a.fechaCreacion).getTime() - new Date(b.fechaCreacion).getTime(),
    },
    {
      title: 'Fecha Transmisión',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 130,
      render: (fecha: string) => new Date(fecha).toLocaleDateString('es-ES'),
      sorter: (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      width: 300,
      ellipsis: true,
      render: (_, record) => record.farmacia?._Name || `ID: ${record.farmaciaId}`,
    },
    {
      title: 'Provincia',
      key: 'provincia',
      width: 120,
      render: (_, record) => record.provincia?.provincia || `ID: ${record.provinciaId}`,
    },
    {
      title: 'Titulares',
      key: 'vendedores',
      width: 250,
      ellipsis: true,
      render: (_, record) => formatTitulares(record.vendedores),
    },
    {
      title: 'Nuevos Titulares',
      key: 'compradores',
      width: 250,
      ellipsis: true,
      render: (_, record) => formatTitulares(record.compradores),
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estadoId',
      width: 110,
      render: (estadoId: number) => (
        <Tag color={getEstadoColor(estadoId)}>{getEstadoLabel(estadoId)}</Tag>
      ),
    },
    {
      title: 'Notas',
      dataIndex: 'notas',
      key: 'notas',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 100,
    },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Listado Gestión Transmisiones
      </Title>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
        <Select
          placeholder="Filtrar por asesor"
          value={selectedAsesor}
          onChange={handleAsesorChange}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 240 }}
          suffixIcon={<UserOutlined />}
          options={asesores.map(a => ({
            value: a.id,
            label: a.nombre,
          }))}
        />
        <Input
          placeholder="Buscar en todos los campos..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>En venta</span>
                  <Badge
                    count={estadoCounts.enVenta}
                    style={{ backgroundColor: '#13c2c2' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: 0
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Pendiente</span>
                  <Badge
                    count={estadoCounts.pendiente}
                    style={{ backgroundColor: '#fa8c16' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: 3
            },
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Realizada</span>
                  <Badge
                    count={estadoCounts.realizada}
                    style={{ backgroundColor: '#52c41a' }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: 5
            }
          ]}
          size="large"
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          Actualizar
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total) => `Total: ${total} registros`,
        }}
        scroll={{ x: 1500 }}
        bordered
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
      />

      <TransmisionDetailModal
        visible={modalVisible}
        transmision={selectedTransmision}
        onClose={handleModalClose}
        onSave={handleSaveTransmision}
      />
    </div>
  );
};

export default TransmisionesPage;
